import * as React from 'react';
import { useTranslation } from 'react-i18next';

// Constants
import { API_URL } from '@src/shared/src/const/api';
// Utils
import { getUserFullname } from '@src/shared/src/util/users';
import { getPolicyViolationMessage } from '@toolkit/util/app';
import { useSelector } from 'react-redux';
// Models
import {
  BookingItemModel,
  PassengerModel,
  PolicyViolationModel,
  RedeemingAllowanceModel,
  RentalModel,
} from '@src/shared/src/models';
// Components
import { WarningBanner, RebookingBanner } from '@toolkit/ui';
import { RedeemedSearchNotification } from './RedeemedSearchNotification';
import { RequiresAllowanceWarning } from './RequiresAllowanceWarning';
import { ExceedsAllowanceWarning } from './ExceedsAllowanceWarning';
import { IRootState } from '@src/store';

type Props = {
  bookingRental: RentalModel;
  currentBookingItems: BookingItemModel[];
  hasConfirmFailedItems: boolean;
  hasHardFailedItems: boolean;
  hasConfirmPendingItems: boolean;
  hasVoidedItems: boolean;
  passengers: PassengerModel[];
  policyViolations: PolicyViolationModel[];
  requiresAllowance: boolean;
  exceedsAllowance: boolean;
  redeemingAllowance: RedeemingAllowanceModel;
  failedMessage: React.ReactNode;
};

const CheckoutBanners: React.FC<Props> = ({
  bookingRental,
  currentBookingItems,
  hasConfirmFailedItems,
  hasHardFailedItems,
  hasConfirmPendingItems,
  hasVoidedItems,
  passengers,
  policyViolations,
  requiresAllowance,
  exceedsAllowance,
  redeemingAllowance,
  failedMessage,
}) => {
  const { t } = useTranslation();

  // We only allow to cancel pending items if they are:
  // 1. ehotel only bookings
  // 2. have a pendingBookingItemId
  // 3. have max 2 passengers
  const pendingItemsCancellable =
    currentBookingItems.length === 1 &&
    passengers.length <= 2 &&
    currentBookingItems[0].isEHotelPending &&
    !!currentBookingItems[0].pendingBookingItemId;

  const showPendingEhotelBanner = hasConfirmPendingItems && pendingItemsCancellable;

  const showFailedBanner =
    !!failedMessage &&
    (hasConfirmFailedItems ||
      hasVoidedItems ||
      (hasConfirmPendingItems && !pendingItemsCancellable)) &&
    !hasHardFailedItems;
  const currentUser = useSelector((state: IRootState) => state.adminUser.profile);

  return (
    <>
      <RebookingBanner />

      {showFailedBanner && (
        <WarningBanner
          type="error"
          label={<span>{failedMessage}</span>}
        />
      )}

      {passengers
        .filter((passenger) => passenger.invalidMembershipCards.length > 0)
        .map((passenger) =>
          passenger.userId === currentUser.id ? (
            <WarningBanner
              type="warning"
              label={
                <div>
                  {t('checkoutConn.notification.invalidMembershipCards.message', {
                    invalidCards: passenger.invalidMembershipCards.join(', '),
                  })}
                  <a
                    className="tcp-link"
                    target="_blank"
                    href={`${API_URL.DEFAULT}/org_admin/users/${passenger.userId}/user_membership_cards`}>
                    {t('checkoutConn.notification.invalidMembershipCards.link')}
                  </a>
                </div>
              }
            />
          ) : (
            <WarningBanner
              type="warning"
              label={
                <div>
                  {t('checkoutConn.notification.userInvalidMembershipCards.message', {
                    travelerName: getUserFullname(passenger),
                  })}
                </div>
              }
            />
          ),
        )
      }

      {showPendingEhotelBanner && (
        <WarningBanner
          type="warning"
          label={
            <>
              <strong>{t('CheckoutConn.pending.info.title')}</strong>
              <div>{t('CheckoutConn.pending.info.message')}</div>
              <a
                className="tcp-link"
                href={`${API_URL.DEFAULT}/org_admin/pending_booking_items/${currentBookingItems[0].pendingBookingItemId}`}>
                {t('CheckoutConn.pending.info.link')}
                <i className="icon-arrow_forward" />
              </a>
            </>
          }
        />
      )}

      {bookingRental?.arrInformation?.length > 0 && (
        <WarningBanner type="info" label={bookingRental.arrInformation} />
      )}

      {!exceedsAllowance && <RedeemedSearchNotification redeemingAllowance={redeemingAllowance} />}

      <RequiresAllowanceWarning requiresAllowance={requiresAllowance} />

      <ExceedsAllowanceWarning
        exceedsAllowance={exceedsAllowance}
        redeemingAllowance={redeemingAllowance}
      />

      {policyViolations?.length > 0 && (
        <WarningBanner
          label={
            <div>
              <p>
                {t('checkoutConn.policy.warning.title', {
                  userName: getUserFullname(passengers[0]),
                })}
              </p>
              <ul className="error">
                {policyViolations.map((warning, index) => (
                  <li key={`$warning-${index}`}>
                    <i className="icon-close2" />
                    {getPolicyViolationMessage(warning)}
                  </li>
                ))}
              </ul>
            </div>
          }
          type="warning"
        />
      )}
    </>
  );
};

export default CheckoutBanners;
